import React, { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CaretDownIcon, CheckIcon, UsersIcon } from '@phosphor-icons/react';

import classNames from '../../../../utils/classnames';
import { popoverPanelClassName } from '../../../../components/form/control';
import { Checkbox } from '../../../../components/checkbox/Checkbox';

export interface PlannerOption {
  id: string;
  name: string;
}

interface PlannerSelectProps {
  planners: PlannerOption[];
  /** Selected planner ids; empty = every planner. */
  selectedIds: string[];
  onChange: (plannerIds: string[]) => void;
}

const ALL_LABEL = 'Alle planners';

function getTriggerLabel(planners: PlannerOption[], selectedIds: string[]): string {
  if (selectedIds.length === 0) return ALL_LABEL;
  if (selectedIds.length === 1) return planners.find((p) => p.id === selectedIds[0])?.name ?? ALL_LABEL;
  return `${selectedIds.length} planners`;
}

/** Scope pill in the page header: filters the fleet to the trucks of one or more planners. */
export const PlannerSelect: React.FC<PlannerSelectProps> = ({ planners, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const isAll = selectedIds.length === 0;

  const toggle = (id: string, checked: boolean) => {
    const next = checked ? [...selectedIds, id] : selectedIds.filter((v) => v !== id);
    // Selecting every planner is the same as no filter.
    onChange(next.length === planners.length ? [] : next);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-brand-border bg-brand-subtle px-3 text-xs font-semibold text-brand-text transition-colors hover:bg-warning-subtle"
          title="Filter op planner"
        >
          <UsersIcon className="h-3.5 w-3.5" weight="bold" />
          <span className="max-w-40 truncate">{getTriggerLabel(planners, selectedIds)}</span>
          <CaretDownIcon className="h-3 w-3" weight="bold" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={4} className={classNames(popoverPanelClassName, 'min-w-56 p-1')}>
          <button
            type="button"
            className={classNames(
              'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm transition-colors hover:bg-surface-hover',
              isAll ? 'font-semibold text-fg' : 'text-fg-secondary',
            )}
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
          >
            <span className="flex-1">{ALL_LABEL}</span>
            {isAll && <CheckIcon className="h-4 w-4 shrink-0 text-brand-text" weight="bold" />}
          </button>

          {planners.length > 0 && <div className="mx-2 my-1 h-px bg-border" />}

          {planners.map((planner) => {
            const isChecked = selectedIds.includes(planner.id);
            return (
              <label
                key={planner.id}
                className={classNames(
                  'flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors hover:bg-surface-hover',
                  isChecked ? 'font-medium text-fg' : 'text-fg-secondary',
                )}
              >
                <Checkbox isChecked={isChecked} onChange={(checked) => toggle(planner.id, checked)} />
                <span className="flex-1 truncate">{planner.name}</span>
              </label>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
